import { Alert, Button, DatePicker, Flex, Form, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import './index.scss';
import { defaultPresets } from '@/utils';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';
import { ArrowDown } from 'lucide-react';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;
const valueFormat = 'YYYY-MM-DD';
const displayFormat = 'DD/MM/YYYY';

function AppRangePicker({
  date,
  onChange,
  onOk,
  dateLimit = {
    value: 1,
    unit: 'year',
  },
  label = 'Period',
  formItemClassName = '',
}) {
  const [open, setOpen] = useState(false);
  const [tempDates, setTempDates] = useState(null);
  const [rangeWarning, setRangeWarning] = useState('');
  const [isRangeValid, setIsRangeValid] = useState(true);
  // const [showAnimation, setShowAnimation] = useState(false);
  const [showWarningAnimation, setShowWarningAnimation] = useState(false);

  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);
  const isMobileXsm = useMediaQuery(`(max-width: ${mediaQueryPoints.xsm}px)`);

  const validateRange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      setRangeWarning('');
      setIsRangeValid(true);
      setShowWarningAnimation(false);
      return;
    }

    const [start, end] = dates;
    const maxUnit = dateLimit;
    const maxAllowed = start.add(maxUnit.value, maxUnit.unit);

    // const wasInvalid = !isRangeValid;

    if (dayjs(end).isAfter(maxAllowed, 'day')) {
      const limitText =
        dateLimit.unit === 'year' ? '1 year' : dateLimit.value + ' ' + dateLimit.unit;
      setRangeWarning(`Date range cannot exceed ${limitText}.`);
      setIsRangeValid(false);

      // trigger warning animation
      setShowWarningAnimation(true);
      setTimeout(() => setShowWarningAnimation(false), 600); // invalid shake animation duration
    } else {
      setRangeWarning('');
      setIsRangeValid(true);
      setShowWarningAnimation(false);

      // trigger success animation if range was invalid then became valid later
      // if (wasInvalid) {
      //   setShowAnimation(true);
      //   setTimeout(() => setShowAnimation(false), 2000);
      // }
    }
  };

  const handleApply = () => {
    if (tempDates?.[0] && tempDates?.[1] && isRangeValid) {
      const formattedDates = [tempDates[0].format(valueFormat), tempDates[1].format(valueFormat)];
      onChange?.(tempDates, formattedDates);
      onOk?.(tempDates, formattedDates);

      setOpen(false);
    }
  };

  const handleCancel = () => {
    if (date) {
      setTempDates([dayjs(date[0], valueFormat), dayjs(date[1], valueFormat)]);
    } else {
      setTempDates(null);
    }

    setRangeWarning('');
    setIsRangeValid(true);
    setShowWarningAnimation(false);

    setOpen(false);
  };

  useEffect(() => {
    if (date) {
      setTempDates([dayjs(date[0], valueFormat), dayjs(date[1], valueFormat)]);
    }
  }, [date]);

  // trigger when range is valid
  // useEffect(() => {
  //   if (isRangeValid && tempDates?.[0] && tempDates?.[1]) {
  //     setShowAnimation(true);
  //     const timer = setTimeout(() => setShowAnimation(false), 2000); // Animation duration
  //     return () => clearTimeout(timer);
  //   }
  // }, [isRangeValid, tempDates]);

  const panelRender = (panelNode) => (
    // <div className={`${isMobile ? 'max-w-[100dvw]' : ''}`}>
    <>
      {!isMobile && <>{panelNode}</>}
      <Space
        className={`w-full justify-end p-2 relative ${isMobile ? 'border-b border-b-[#f0f0f0]' : 'border-t border-t-[#f0f0f0] pt-4'}`}
      >
        {rangeWarning && (
          <Alert
            message={rangeWarning}
            type="warning"
            showIcon
            className={`absolute top-2 left-2 ${showWarningAnimation ? 'animate-shake' : ''} custom-alert`}
            style={{
              animation: showWarningAnimation ? 'shake 0.6s ease-in-out' : undefined,
              fontSize: isMobile ? '10px' : '14px',
            }}
          />
        )}
        {/* {showAnimation && isRangeValid && (
          <Alert
            message="Valid date range selected!"
            type="success"
            showIcon
            className="absolute top-2 left-2 animate-pulse"
            style={{
              animation: 'fadeInOut 2s ease-in-out',
            }}
          />
        )} */}
        <div className={`relative flex items-center gap-2 justify-end h-[32px]`}>
          <div className={`${isMobile ? '' : ''} flex items-center gap-2 justify-end`}>
            <Button type="text" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleApply}
              disabled={!tempDates?.[0] || !tempDates?.[1] || !isRangeValid}
            >
              Apply
            </Button>
          </div>
        </div>
      </Space>
      {isMobile && <>{panelNode}</>}
      {/* </div> */}
    </>
  );

  return (
    <Form.Item label={label} className={`${formItemClassName}`}>
      <RangePicker
        format={displayFormat}
        allowClear={false}
        value={tempDates ? [tempDates[0], tempDates[1]] : null}
        open={open}
        onOpenChange={(o) => {
          setOpen(true);
          if (o) {
            setTempDates(null);
          }
        }}
        onCalendarChange={(dates) => {
          setTempDates(dates);
          validateRange(dates);
        }}
        onChange={(dates) => {
          if (!dates || dates.length === 0) {
            setTempDates(null);
            onChange?.(null, null);

            if (date) {
              onOk?.(null, date);
            } else {
              onOk?.(null, null);
            }

            setOpen(false);
          }
        }}
        panelRender={panelRender}
        // presets={isMobile ? null : defaultPresets}
        presets={defaultPresets}
        inputReadOnly
        className="cursor-pointer w-full"
        disabledDate={(currentDate) => {
          return dayjs(currentDate).isAfter(dayjs(), 'day');
        }}
      />
    </Form.Item>
  );
}

export default AppRangePicker;
